<script setup lang="ts">
import { ref } from 'vue';
import { Config } from '@/config';
import { useRoute } from 'vue-router';
import { constant } from '@/constant';
import { getLabel } from '@/utils/data-display-utils';

import mobileMenu from '@/components/mobile/mobileMenu.vue';
import LoginDialog from '@/components/dialogs/LoginDialog.vue';
import BaseDialog from '@/components/base/BaseDialog.vue';
import AccountSection from '@/components/AccountSection.vue';
import LocaleSelect from '@/components/LocaleSelect.vue';
import RoundLogoImage from '@/components/RoundLogoImage.vue';

import { useUserStore } from '@/store/userStore';
import { useCommunityStore } from '@/store/communityStore';
import { paths } from '@/utils/paths';
import { authService } from '@/services/auth.service';
import useResponsive from '@/utils/responsive-util';
import { useTracker } from '@/composables/useTracker';
import { getAltText } from '@/utils/image.helper';
import { Locales } from '@/store/language.interface';

// PROPS
const props = defineProps<{
  languageEnabled?: Locales[];
}>();

// STORE
const userStore = useUserStore();
const communityStore = useCommunityStore();

// REFS
const loginDialogRef = ref<InstanceType<typeof BaseDialog>>();

// CONST
const { isMobile } = useResponsive();
const results = ref(false);
const route = useRoute();
const { track } = useTracker();

// EMITS
const emit = defineEmits<{
  (e: 'results', value): void;
}>();

// FUNCTIONS
function onLogin() {
  track({
    event: 'login_click',
    params: {
      location: 'header',
    },
  });
  authService.login();
}

const openAdmin = () => {
  window.open(`${Config.admin.url}`, '_blank');
};

function openResults() {
  results.value = !results.value;
  emit('results', results.value);
}

function shouldDisplayAdmin() {
  return userStore.isAdmin() || userStore.isEditor();
}

function shouldDisplayResults() {
  if (!route.path.includes('consultation')) {
    return false;
  } else if (userStore.isAdmin()) {
    return true;
  }
  const slug = route.params.slug as string;
  const consultation = communityStore.community.consultations.find((x) => x.slug === slug);
  return userStore.isEditor(constant.HomePageItem.Type.Consultation, consultation?.id);
}
</script>

<template>
  <div class="header">
    <div class="header__items">
      <router-link class="header__logo" :to="paths.root">
        <RoundLogoImage
          :source="communityStore.community.logoFile.url"
          :color="communityStore.community.logoFile.color"
          :alt-text="getAltText(communityStore.community.logoFile, 'alt-text.community.logo', props.languageEnabled)"
        />
      </router-link>

      <template v-if="isMobile">
        <div class="header__mobile">
          <AccountSection v-if="userStore.isUserLogged" :language-enabled="languageEnabled" />
          <mobile-menu @open-login-dialog="onLogin()" :language-enabled="languageEnabled" />
        </div>
      </template>

      <template v-else>
        <div class="header__navigation">
          <q-badge v-if="shouldDisplayResults()" color="secondary" class="q-mr-md cursor-pointer" @click="openResults"
            >{{ getLabel('consultation.results', props.languageEnabled) }}
          </q-badge>
          <q-badge v-if="shouldDisplayAdmin()" color="primary" class="q-mr-md cursor-pointer" @click="openAdmin"
            >admin
          </q-badge>
          <router-link class="header__navigation--item" :to="paths.about">
            {{ getLabel('about-us', props.languageEnabled) }}
          </router-link>
          <q-separator vertical />
          <div class="header__navigation--locale">
            <LocaleSelect v-if="languageEnabled?.length > 1" :language-enabled="languageEnabled" />
          </div>
          <q-separator v-if="languageEnabled?.length > 1" vertical />
          <AccountSection v-if="userStore.isUserLogged" :language-enabled="languageEnabled" />
          <div v-else class="header__navigation--item" @click="onLogin()">
            {{ getLabel('account.login', props.languageEnabled) }}
          </div>
        </div>
      </template>
    </div>
  </div>
  <LoginDialog ref="loginDialogRef" :language-enabled="languageEnabled" />
</template>

<style lang="scss">
.header {
  z-index: 2;
  position: -webkit-sticky;
  position: sticky !important;
  top: 0;
  align-items: center;
  background-color: $color-neutral-white;
  box-shadow: 0 2px 10px rgba(1, 0, 0, 0.1);
  height: $header-height;

  &__items {
    height: inherit;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 32px;
  }

  &__logo {
    width: 60px;
    height: 60px;

    &:hover {
      border-bottom: 1px solid transparent;
    }
  }

  &__navigation {
    display: flex;
    align-items: center;

    &--item {
      text-decoration: none;
      border-bottom: 1px solid transparent;
      color: $color-neutral-black;
      transition: all 200ms ease;
      cursor: pointer;

      &:hover {
        color: $color-primary;
        border-bottom: 1px solid transparent;
        transition: all 200ms ease;
      }
    }

    &--icon {
      margin-left: 24px;
      height: 24px !important;
      width: 24px !important;
    }
  }

  .q-separator {
    margin: $space-xs $space-md;
    height: 32px;
  }

  &__menu {
    .q-item__section {
      margin: 0 16px;
    }
  }
}

@media screen and (max-width: $breakpoint-xl) {
  .header {
    &__mobile {
      display: flex;
    }

    &__items {
      margin: 0 20px;
    }
  }
}

@media screen and (max-width: $breakpoint-sm) {
  .header {
    height: 56px;

    &__items {
      margin: 0 10px;
    }

    &__logo {
      width: 40px;
      height: 40px;
    }
  }
}
</style>
